import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, AlertTriangle } from "lucide-react";
import { PHASES, phaseStatus, NEXT_ACTION_BY_STATE } from "../lib/caseFlowMap";

/**
 * CaseFlowStepper — Stepper visual de las 3 fases: Prevalidación → Diagnóstico → Documentos.
 *
 * Iter20 — Visible en CaseDetailPage, PrevalidationPage, PaymentPage,
 *          Nivel2RequestPage, PaymentN2Page. Da contexto inmediato al usuario.
 *
 * Props:
 *  - caseId: string
 *  - state: estado actual del caso (e.g. "nivel1_payment_pending")
 *  - timeline: { prevalidation_at, diagnosis_at, kit_at } (opcional — hitos)
 *  - compact: boolean — versión más pequeña (sin labels) para top-bar
 */
export default function CaseFlowStepper({ caseId, state, timeline = {}, compact = false }) {
  const action = NEXT_ACTION_BY_STATE[state];

  const renderIcon = (status) => {
    if (status === "done") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (status === "active") return <Loader2 className="w-4 h-4 text-brand-burgundy animate-spin" />;
    if (status === "blocked") return <AlertTriangle className="w-4 h-4 text-rose-600" />;
    return <Circle className="w-4 h-4 text-gray-400" />;
  };

  const colorClasses = (status) => {
    if (status === "done") return "border-emerald-300 bg-emerald-50/50 text-emerald-800";
    if (status === "active") return "border-brand-burgundy bg-brand-cream-soft text-brand-burgundy shadow-sm";
    if (status === "blocked") return "border-rose-300 bg-rose-50 text-rose-800";
    return "border-gray-200 bg-white text-gray-500";
  };

  const timestamp = (phase) => {
    const ts = timeline[`${phase}_at`];
    if (!ts) return null;
    try {
      const d = new Date(ts);
      return d.toLocaleDateString("es-CO", { day: "numeric", month: "short" });
    } catch {
      return null;
    }
  };

  return (
    <div
      className={`bg-white border border-gray-200 ${compact ? "p-3" : "p-4 sm:p-5"} rounded-sm`}
      data-testid="case-flow-stepper"
    >
      <div className="flex items-center gap-2 mb-3">
        {PHASES.map((phase, idx) => {
          const status = phaseStatus(state, phase.key);
          return (
            <React.Fragment key={phase.key}>
              <div
                className={`flex-1 border ${colorClasses(status)} px-3 py-2 transition-colors`}
                data-testid={`stepper-phase-${phase.key}`}
                data-status={status}
              >
                <div className="flex items-center gap-2">
                  {renderIcon(status)}
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] uppercase tracking-widest opacity-70 truncate">{phase.short}</p>
                    {!compact && (
                      <p className="text-sm font-medium truncate">{phase.label}</p>
                    )}
                  </div>
                </div>
                {timestamp(phase.key) && !compact && (
                  <p className="text-[10px] opacity-60 mt-1">{timestamp(phase.key)}</p>
                )}
              </div>
              {idx < PHASES.length - 1 && (
                <div className={`h-0.5 w-4 sm:w-8 ${phaseStatus(state, PHASES[idx + 1].key) === "todo" ? "bg-gray-200" : "bg-emerald-300"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {action && !compact && (
        <div className="border-t border-gray-100 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-xs text-gray-500">
            <span className="uppercase tracking-widest text-[10px] mr-1">Próximo paso</span>
            <span className="text-gray-800 font-medium">{action.label}</span>
          </div>
          {action.path && (
            <Link
              to={action.path(caseId)}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                action.variant === "primary"
                  ? "bg-brand-burgundy text-white border-brand-burgundy hover:bg-brand-burgundy-light"
                  : action.variant === "warn"
                  ? "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                  : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
              }`}
              data-testid="stepper-next-action"
            >
              {action.label} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
